import { useCallback, useMemo, useState, type ReactNode } from "react";
import { DrawerStackContext } from "./DrawerStackContext";
import { DrawerStackHost } from "./DrawerStackHost";
import type { DrawerStackEntry } from "./types";

let stackIdCounter = 0;
export function generateStackId(): string {
  return `drawer-stack-${++stackIdCounter}-${Date.now()}`;
}

interface LocalDrawerStackProviderProps {
  children: ReactNode;
  maxDepth: number;
}

/** Drawer stack kept in React state. Used when `DrawerStackProvider` has no `routes` or router. */
export function LocalDrawerStackProvider({
  children,
  maxDepth,
}: LocalDrawerStackProviderProps) {
  const [stack, setStack] = useState<DrawerStackEntry[]>([]);

  const pushDrawer = useCallback(
    (entry: Omit<DrawerStackEntry, "id"> & { id?: string }) => {
      setStack((prev) => {
        if (prev.length >= maxDepth) {
          console.warn(
            `[DrawerStack] Maximum depth (${maxDepth}) reached. Cannot push more drawers.`,
          );
          return prev;
        }
        // If a drawer with the same id already exists, don't push a duplicate
        const id = entry.id ?? generateStackId();
        if (prev.some((d) => d.id === id)) {
          return prev;
        }
        return [...prev, { ...entry, id }];
      });
    },
    [maxDepth],
  );

  const popDrawer = useCallback(() => {
    setStack((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev));
  }, []);

  const closeAll = useCallback(() => {
    setStack([]);
  }, []);

  const closeFrom = useCallback((id: string) => {
    setStack((prev) => {
      const index = prev.findIndex((d) => d.id === id);
      if (index === -1) return prev;
      return prev.slice(0, index);
    });
  }, []);

  const contextValue = useMemo(
    () => ({ stack, pushDrawer, popDrawer, closeAll, closeFrom }),
    [stack, pushDrawer, popDrawer, closeAll, closeFrom],
  );

  return (
    <DrawerStackContext.Provider value={contextValue}>
      {children}
      <DrawerStackHost />
    </DrawerStackContext.Provider>
  );
}
